import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams, Navigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, Search as SearchIcon, SlidersHorizontal, MapPin, X, Loader2 } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { ListingCard } from "@/components/ListingCard";
import { FilterSheet, Pill } from "@/components/category/FilterSheet";
import { useCachedGet } from "@/lib/cache";
import { usePagedListings, LoadMoreSentinel } from "@/lib/pagedListings";
import { CATEGORIES, EMIRATES } from "@/lib/constants";
import { EMIRATE_FILTER_CATS, PROVIDER_EMIRATES, DW_NATIONALITY_NAMES, SUB_FILTER_KEY, PRICE_TITLE, CAT_SORTS, CONTRACTS, GENDERS, FILTER_KEYS, csv } from "@/lib/categoryFilters";
import { useLang } from "@/context/LanguageContext";

const PREFS = (id) => `bazaro_listprefs_category:${id}`;

const Grid = ({ items, testid, prefix }) => (
  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 lg:gap-6" data-testid={testid}>
    {items.map((l) => <div key={l.id} data-testid={`${prefix}-${l.id}`}><ListingCard listing={l} /></div>)}
  </div>
);

export default function CategoryAds() {
  const { catId } = useParams();
  const navigate = useNavigate();
  const { lang, dir } = useLang();
  const L = (ar, en) => (lang === "en" ? en : ar);
  const nm = (i) => (lang === "en" ? i.name_en : i.name_ar);
  const cat = CATEGORIES.find((c) => c.id === catId);
  const [sp, setSp] = useSearchParams();
  const p = Object.fromEntries(FILTER_KEYS.map((k) => [k, sp.get(k) || ""]));
  const [qText, setQText] = useState(p.q);
  const [sheet, setSheet] = useState(false);
  const { data: meta } = useCachedGet("/meta");
  const isDW = catId === "domestic_workers";

  const spRef = useRef(sp);
  spRef.current = sp;
  const savePrefs = (n) => localStorage.setItem(PREFS(catId), JSON.stringify({ sort: n.get("sort") || "newest", verified: n.get("verified") === "1" }));
  const set = (patch, save = true) => {
    const n = new URLSearchParams(spRef.current);
    Object.entries(patch).forEach(([k, v]) => (v ? n.set(k, v) : n.delete(k)));
    spRef.current = n;
    setSp(n, { replace: true });
    if (save && ("sort" in patch || "verified" in patch)) savePrefs(n);
  };
  const toggle = (k, v) => { const a = csv(spRef.current.get(k)); set({ [k]: (a.includes(v) ? a.filter((x) => x !== v) : [...a, v]).join(",") }); };
  const clearAll = () => { setQText(""); set(Object.fromEntries(FILTER_KEYS.map((k) => [k, ""]))); };

  useEffect(() => { const t = setTimeout(() => qText !== p.q && set({ q: qText.trim() }), 400); return () => clearTimeout(t); }, [qText]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (sp.has("sort") || sp.has("verified")) return;
    try { const pr = JSON.parse(localStorage.getItem(PREFS(catId))) || {}; if ((pr.sort && pr.sort !== "newest") || pr.verified) set({ sort: pr.sort && pr.sort !== "newest" ? pr.sort : "", verified: pr.verified ? "1" : "" }, false); } catch { /* none */ }
  }, [catId]); // eslint-disable-line react-hooks/exhaustive-deps

  const filterKey = SUB_FILTER_KEY[p.sub];
  const serviceFilter = filterKey ? (meta?.subcat_fields?.[p.sub] || []).find((f) => f.key === filterKey) : null;
  const showCondition = p.sub === "gym_equipment" || p.sub === "game_tables";
  const params = useMemo(() => {
    const o = { category: catId, sort: p.sort || "newest" };
    if (p.sub) o.subcategory = p.sub;
    if (p.q) o.q = p.q;
    if (p.verified) o.verified_only = true;
    if (p.disc) o.discounted = true;
    if (p.min_price) o.min_price = p.min_price;
    if (p.max_price) o.max_price = p.max_price;
    if (p.em) o.emirate = p.em;
    if (filterKey && p.fv) o.ff = `${filterKey}:${p.fv}`;
    if (showCondition && p.cond) o.condition = p.cond;
    if (isDW) { if (p.skills) o.skills = p.skills; if (p.ct) o.contract = p.ct; if (p.nat) o.nationality = p.nat; if (p.langs) o.langs = p.langs; if (p.g) o.gender = p.g; }
    return o;
  }, [sp.toString(), catId, filterKey, showCondition, isDW]); // eslint-disable-line react-hooks/exhaustive-deps
  const paged = usePagedListings(params, { enabled: !!cat });
  const { loading } = paged;
  const ads = paged.items;

  const chipsList = [
    p.sub && cat?.subcategories.find((s) => s.id === p.sub) && { key: "sub", label: nm(cat.subcategories.find((s) => s.id === p.sub)), clear: { sub: "", fv: "", cond: "" } },
    p.verified && { key: "verified", label: L("بائعون موثّقون فقط", "Verified sellers only"), clear: { verified: "" } },
    p.disc && { key: "disc", label: L("المخفّضة فقط", "Discounted only"), clear: { disc: "" } },
    p.sort && p.sort !== "newest" && { key: "sort", label: L(...CAT_SORTS.find((s) => s[0] === p.sort)?.slice(1) || [p.sort, p.sort]), clear: { sort: "" } },
    (p.min_price || p.max_price) && { key: "price", label: `${p.min_price || 0} - ${p.max_price || "∞"}`, clear: { min_price: "", max_price: "" } },
    isDW && p.skills && { key: "skills", label: `${L("يجيد أيضاً", "Skills")} (${csv(p.skills).length})`, clear: { skills: "" } },
    isDW && p.g && { key: "gender", label: L(...GENDERS.find((c) => c[0] === p.g)?.slice(1) || [p.g, p.g]), clear: { g: "" } },
    isDW && p.ct && { key: "contract", label: L(...CONTRACTS.find((c) => c[0] === p.ct)?.slice(1) || [p.ct, p.ct]), clear: { ct: "" } },
    isDW && p.nat && { key: "nat", label: lang === "en" ? p.nat : (meta?.countries || []).find((c) => c[1] === p.nat)?.[0] || p.nat, clear: { nat: "" } },
    isDW && p.langs && { key: "langs", label: `${L("اللغات", "Languages")} (${csv(p.langs).length})`, clear: { langs: "" } },
    serviceFilter && p.fv && { key: "service", label: (() => { const o = serviceFilter.options?.find((x) => x.id === p.fv); return o ? L(o.ar, o.en) : p.fv; })(), clear: { fv: "" } },
    showCondition && p.cond && { key: "cond", label: p.cond === "new" ? L("جديد", "New") : L("مستعمل", "Used"), clear: { cond: "" } },
  ].filter(Boolean);
  const hasFilters = chipsList.length > 0 || !!p.em || !!p.q;

  const nearbyOn = !loading && ads.length === 0 && hasFilters;
  const { data: nearby } = useCachedGet("/listings", { category: catId, sort: p.sort || "newest" }, { enabled: !!cat && nearbyOn });

  if (!cat) return <Navigate to="/categories" replace />;
  const Back = dir === "rtl" ? ArrowRight : ArrowLeft;
  const emirateChips = isDW || catId === "home_services" ? EMIRATES.filter((e) => PROVIDER_EMIRATES.includes(e.en)) : EMIRATES;
  const nationalities = DW_NATIONALITY_NAMES.map((n) => (meta?.countries || []).find((c) => c[1] === n) || [n, n]);
  const ctx = { isDW, serviceFilter, showCondition, priceTitle: PRICE_TITLE[p.sub] && L(...PRICE_TITLE[p.sub]), nationalities, languages: meta?.languages || [] };

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <Navbar />
      <div className="bg-white border-b border-[#EFEBE0]" data-testid="category-ads-header">
        <div className="max-w-7xl 2xl:max-w-[1600px] mx-auto px-4 sm:px-8 py-3 space-y-3">
          <div className="flex items-center gap-3">
            <button onClick={() => (window.history.length > 1 ? navigate(-1) : navigate(`/category/${catId}`))} className="w-10 h-10 rounded-full hover:bg-[#F4F2EC] flex items-center justify-center" data-testid="back-btn"><Back className="w-5 h-5" /></button>
            <h1 className="flex-1 text-center font-headline text-lg font-bold text-slate-900" data-testid="category-ads-title">{nm(cat)}</h1>
            <span className="w-10" />
          </div>
          <label className="flex items-center gap-2 h-12 rounded-full border border-[#E7E3D8] bg-[#FAF8F3] px-4 focus-within:border-[#1A5C3A]">
            <SearchIcon className="w-4 h-4 text-slate-400 shrink-0" />
            <input value={qText} onChange={(e) => setQText(e.target.value)} placeholder={L(`ابحث في ${cat.name_ar}...`, `Search in ${cat.name_en}...`)} className="flex-1 bg-transparent outline-none text-sm" data-testid="category-search-input" />
            {qText && <button type="button" onClick={() => setQText("")} data-testid="category-search-clear"><X className="w-4 h-4 text-slate-400" /></button>}
          </label>
          {EMIRATE_FILTER_CATS.includes(catId) && (
            <div className="flex items-center gap-2 overflow-x-auto no-scrollbar" data-testid="emirate-filter">
              <span className="flex items-center gap-1 shrink-0 text-[13px] font-bold text-slate-500" data-testid="emirate-filter-label"><MapPin className="w-3.5 h-3.5 text-[#1A5C3A]" />{L("اختر الإمارة", "Choose emirate")}</span>
              <Pill active={!p.em} onClick={() => set({ em: "" })} testid="emirate-filter-all">{L("الكل", "All")}</Pill>
              {emirateChips.map((e) => <Pill key={e.en} active={p.em === e.ar} onClick={() => set({ em: e.ar })} testid={`emirate-filter-${e.en}`}>{L(e.ar, e.en)}</Pill>)}
            </div>
          )}
          {isDW && (
            <div className="flex items-center gap-2 overflow-x-auto no-scrollbar" data-testid="gender-filter">
              <span className="shrink-0 text-[13px] font-bold text-slate-500" data-testid="gender-filter-label">{L("الجنس", "Gender")}</span>
              {GENDERS.map(([id, ar, en]) => <Pill key={id || "all"} active={p.g === id} onClick={() => set({ g: id })} testid={`gender-filter-${id || "all"}`}>{L(ar, en)}</Pill>)}
            </div>
          )}
          <div className="flex items-center gap-2">
            <button onClick={() => setSheet(true)} className={`shrink-0 h-10 px-4 rounded-full border text-[13px] font-bold flex items-center gap-1.5 ${chipsList.length ? "bg-[#1A5C3A] text-white border-[#1A5C3A]" : "bg-[#F4F2EC] text-slate-800 border-[#E7E3D8]"}`} data-testid="filters-open">
              <SlidersHorizontal className="w-4 h-4" />{L("الفلاتر", "Filters")}{chipsList.length ? ` (${chipsList.length})` : ""}
            </button>
            <div className="flex gap-2 overflow-x-auto no-scrollbar">
              {chipsList.map((c) => (
                <button key={c.key} onClick={() => set(c.clear)} className="shrink-0 h-9 px-3 rounded-full bg-[#E8EFEA] border border-[#1A5C3A] text-[#1A5C3A] text-xs font-bold flex items-center gap-1" data-testid={`active-chip-${c.key}`}>{c.label}<X className="w-3.5 h-3.5" /></button>
              ))}
            </div>
          </div>
        </div>
      </div>
      <main className="max-w-7xl 2xl:max-w-[1600px] mx-auto px-4 sm:px-8 w-full py-6 flex-1" data-testid="category-ads-page">
        {loading ? <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-[#1A5C3A]" /></div>
          : ads.length ? <>
            <p className="text-sm text-slate-500 mb-4" data-testid="category-ads-count">{L(`${paged.total} إعلان`, `${paged.total} ${paged.total === 1 ? "ad" : "ads"}`)}</p>
            <Grid items={ads} testid="category-ads-grid" prefix="cat-ad" />
            <LoadMoreSentinel hasMore={paged.hasMore} busy={paged.busy} onMore={paged.loadMore} count={ads.length} />
          </> : <>
            <div className="text-center py-12" data-testid="category-ads-empty">
              <p className="text-slate-500">{L("لا توجد إعلانات", "No ads")}</p>
              {hasFilters && <button onClick={clearAll} className="mt-3 h-10 px-5 rounded-full border border-[#1A5C3A] text-[#1A5C3A] text-sm font-bold" data-testid="category-ads-empty-clear">{L("مسح الفلاتر", "Clear filters")}</button>}
            </div>
            {nearbyOn && nearby?.length > 0 && <div data-testid="nearby-ads"><h2 className="font-bold text-slate-900 mb-4">{L("إعلانات قريبة من نفس القسم", "Nearby ads from this category")}</h2><Grid items={nearby.slice(0, 10)} testid="nearby-ads-grid" prefix="nearby-ad" /></div>}
          </>}
      </main>
      <FilterSheet open={sheet} onClose={() => setSheet(false)} cat={cat} p={p} set={set} toggle={toggle} ctx={ctx} total={paged.total} hasFilters={hasFilters} clearAll={clearAll} L={L} nm={nm} />
      <Footer />
    </div>
  );
}
